<template>
  <Modal :style="computedWindowSize"
         :visible="visible"
         class="p-fluid"
         @close="close">
    <template v-slot:header>
      <div class="p-dialog-title">{{ item?.id ? $t('taxEditModalTitle') : $t('taxCreateModalTitle') }}</div>
    </template>
    <Spinner v-if="dataIsSending"/>
    <div v-else class="p-formgrid p-grid p-pt-3">
      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <div class="p-float-label">
          <!--          suffix="%"-->
          <InputNumber :locale="computedNumberInputLocale"
                       id="tax-modal-value"
                       :inputClass="{'p-invalid' : submitted && !taxData.value}"
                       v-model="taxData.value"
                       mode="decimal"
                       :minFractionDigits="2"
                       :maxFractionDigits="2"
                       :min="0"
                       :max="100"
                       showButtons
                       autocomplete="off"/>
          <label for="tax-modal-value">{{ $t('Value') }} (%)<span class="warning-color">*</span></label>
        </div>
        <small class="p-invalid" v-if="submitted && !taxData.value && taxData.value !== 0">{{ $t('Required field') }}</small>
      </div>
      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <div class="p-float-label">
          <InputText id="tax-modal-name" v-model.trim="taxData.name" autocomplete="off"/>
          <label for="tax-modal-name">{{ $t('Name') }}</label>
        </div>
      </div>
      <div class="p-field p-col-12 p-sm-12 p-md-12 p-lg-12 p-mb-5">
        <div class="p-float-label">
          <Calendar id="tax-modal-valid-until"
                    appendTo="self"
                    class="calendar-bottom"
                    @date-select="changeValidUntilDate"
                    @blur="changeValidUntilDate"
                    @show="toggleDropdownOverlayVisibility(true)"
                    @hide="toggleDropdownOverlayVisibility(false)"
                    v-model="taxData.valid_until"
                    :dateFormat="settings.dateFormat"
                    :showIcon="true"
                    :monthNavigator="true"
                    :yearNavigator="true"
                    :showButtonBar="true"
                    :yearRange="(new Date().getFullYear() - 5) + ':' + (new Date().getFullYear() + 1)"
                    :selectOtherMonths="true"
                    autocomplete="off"/>
          <label for="tax-modal-valid-until">{{ $t('Valid until') }}</label>
        </div>
      </div>

      <div class="p-field p-col-12 p-sm-6 p-md-6 p-lg-6">
        <div class="p-d-flex p-ai-center">
          <div class="p-d-flex p-ai-center">
            <InputSwitch id="tax-modal-is-active" v-model="isActive"/>
          </div>
          <label for="tax-modal-is-active" class="switch-label pointer">{{ $t('active') }}</label>
        </div>
      </div>

      <div v-if="error" class="p-col-12 p-mt-3 p-mb-3">
        <InlineNotice severity="error" :compact="true">
          {{ error }}
        </InlineNotice>
      </div>
    </div>


    <template #footer>
      <Button :label="$t('Cancel')" icon="pi pi-times" class="p-button-text" @click="close"/>
      <Button :label="$t('Save')"
              :disabled="dataIsSending || disableSaveButton"
              :loading="dataIsSending"
              icon="pi pi-check"
              class="p-button-text"
              @click="saveItem"/>
    </template>
  </Modal>
</template>

<script>
import httpClient from '@/services/http.services'
import formatMixins from "@/mixins/formatMixins";
import overlayVisibilityMixins from "@/mixins/overlayVisibilityMixins";
import settings from "@/settings";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import InlineNotice from '@/components/InlineNotice.vue'

export default {
  mixins: [ showErrorsMixins, formatMixins, overlayVisibilityMixins ],
  components: { InlineNotice },
  emits: ['close', 'update-items'],
  name: 'TaxModal',
  props: {
    item: Object,
    visible: Boolean,
  },
  data() {
    return {
      error: null,
      dataIsSending: false,
      disableSaveButton: false,
      taxData: {},
      submitted: false,
      isActive: false,
      settings
    }
  },
  watch: {
    item(value) {
      // if (!value) return
      // this.taxData = { ...value }
      // if (value.value) {
      //   this.taxData.value = +value.value
      // }
      //
      // if (value.valid_until) {
      //   this.taxData.valid_until = new Date(value.valid_until * 1000)
      // } else {
      //   this.taxData.valid_until = null
      // }
      //
      // this.isActive = !value.id || value.status === 1

      this.taxData = value ? { ...value } : {}
      this.taxData.value = value?.value !== undefined && value?.value !== null ? +value.value : null
      this.taxData.valid_until = value?.valid_until ? new Date(value.valid_until * 1000) : null
      this.isActive = !value?.id || Number(value.status) === 1
    },
    visible() {
      if (!this.visible) {
        this.disableSaveButton = false
        this.dataIsSending = false
        this.submitted = false
        this.error = null
      }
    },
  },
  methods: {
    changeValidUntilDate() {
      if (!this.taxData.valid_until) return false
    },
    async saveItem() {
      if (this.dataIsSending) return

      this.submitted = true
      if (!this.taxData?.value && this.taxData.value !== 0) {
        return false
      }

      this.disableSaveButton = true
      this.dataIsSending = true

      const tax = {
        id: this.taxData.id ?? null,
        name: this.taxData.name ? this.taxData.name : null,
        value: this.taxData.value ?? null,
        status: this.isActive ? 1 : 2,
        validUntil: this.toPhpTimestamp(this.taxData.valid_until)
      }

      if (this.taxData.id) {
        try {
          const { status, data } = await httpClient.post(`tax/update`, tax)
          if (status === 200 && data?.success) {
            this.$emit('update-items', this.taxData.id)
            this.$toast.add({severity: 'success', detail: this.$t('Data updated'), life: settings.toastLife});
            this.close()
          } else {
            this.showUnknownError()
          }
        } catch(err) {
          const summary = err.response?.data?.error?.summary
          if (summary) {
            if (summary === 'Tax value is locked') {
              this.error = this.$t('taxValueIsLockedError')
            } else if (summary === 'Tax is in use') {
              this.error = this.$t('taxIsInUseError')
            }
          } else {
            this.showError(err)
          }
        } finally {
          this.dataIsSending = false
          this.disableSaveButton = false
        }
      } else {
        try {
          const { status, data } = await httpClient.post(`tax/create`, tax)
          if (status === 201 && data?.success) {
            this.taxData.id = data.id
            tax.id = data.id
            this.$emit('update-items', this.taxData.id)
            this.$toast.add({severity:'success', detail: this.$t('Data saved'), life: settings.toastLife});
            this.close()
          } else {
            this.showUnknownError()
          }
        } catch(err) {
          this.showError(err)
        } finally {
          this.dataIsSending = false
          this.disableSaveButton = false
        }
      }
    },
    close() {
      this.$emit('close')
      this.submitted = false
    },
  },
  computed: {
    computedWindowSize() {
      if (this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return {position: 'absolute', top: 0, width: '95%', maxWidth: '380px', marginTop: '10px'}
      } else if (!this.$store.state.mobileLayout && this.$store.state.tabletLayout) {
        return {position: 'absolute', top: 0, width: '380px', marginTop: '25px'}
      } else {
        return {position: 'absolute', top: 0, width: '380px', marginTop: '50px'}
      }
    },
  },

}
</script>